import {
  DynamicPriceQuizQuestions,
  DynamicPriceQuizResults,
  StackingCardsFlow,
  StackingQuestionCards,
  StackingResultCards
} from 'features/dynamic-tariff/components/quiz/types'
import { startSimulationOrRegistration } from 'utils/customerFlow'
import { calculateScoreForResult } from 'features/dynamic-tariff/utils'
import { PORTAL_URL } from 'constants/constants'
import { Language } from 'types/language'

export const ACTIVATE_IN_PORTAL_URL = `${PORTAL_URL}/addresses`

export const QUIZ_QUESTION_CARDS: StackingQuestionCards<DynamicPriceQuizQuestions> = {
  [DynamicPriceQuizQuestions.LOCATION]: {
    title: 'Woon je in Vlaanderen of Brussel?',
    description: 'In Wallonië kan je nog niet kiezen voor dynamische prijzen.'
  },
  [DynamicPriceQuizQuestions.METER]: {
    title: 'Heb je een digitale meter?',
    description:
      'Een digitale meter is nodig voor dynamische prijzen. Die leest namelijk per kwartier je verbruik uit aan de prijzen van dat moment, betalen doe je per uur.'
  },
  [DynamicPriceQuizQuestions.SOLAR_PANELS_OR_HOME_BATTERY]: {
    title: 'Heb je zonnepanelen én een slimme thuisbatterij?',
    description:
      'Met de combinatie van zonnepanelen en een thuisbatterij, kan je de kost voor energie-afname van het algemeen stroomnet verlagen. Met dynamische prijzen kan je via je batterij energie van het net halen, wanneer de marktprijzen laag zijn.'
  },
  [DynamicPriceQuizQuestions.ELECTRIC_VEHICLE]: {
    title: 'Heb je een elektrisch voertuig of plan je die aan te schaffen?',
    description:
      'Een elektrisch voertuig thuis opladen heeft een grote impact op je verbruik. Wanneer je jouw verbruik verschuift naar de momenten waarop de energieprijs op de markt laag staat, kan je geld uitsparen.'
  },
  [DynamicPriceQuizQuestions.SMART_HOME]: {
    title: 'Kan je jouw grootverbruikers plannen?',
    description:
      'Door elektriciteit te gebruiken wanneer de prijzen lager zijn, kan je besparen. Heb je slimme apparaten thuis? Dan kan je je verbruik makkelijk timen, ook al ben je er niet. Slimme stopcontacten kunnen ook helpen. Via My Bolt zie je in een oogopslag wanneer de voordeligste uren precies zijn.'
  }
}

export const QUIZ_RESULT_CARDS: StackingResultCards<DynamicPriceQuizResults> = {
  [DynamicPriceQuizResults.UNAVAILABLE_FLANDERS_ONLY]: {
    title: 'Dynamische prijzen zijn op dit moment enkel beschikbaar in Vlaanderen en Brussel',
    description:
      'Woon je in Wallonië? Op onze tarievenpagina krijg je een helder overzicht van de Bolt-tarieven die mogelijk zijn in deze regio.',
    actions: {
      primary: {
        text: 'Bekijk onze tarieven',
        internalRoute: 'pricing'
      }
    }
  },
  [DynamicPriceQuizResults.UNAVAILABLE_DIGITAL_METER_REQUIRED]: {
    title: 'Dynamische prijzen zijn enkel mogelijk met een digitale meter',
    description:
      'De netbeheerder zal jou zelf contacteren voor de plaatsing van een digitale meter. Tegen 2029 zal iedereen in Vlaanderen een digitale meter hebben. Wil je niet langer wachten? Dan kan je zelf een aanvraag doen bij jouw netbeheerder.',
    actions: {
      primary: {
        text: 'Contacteer de netbeheerder',
        externalUrl: (language) =>
          language === Language.FRENCH
            ? 'https://www.synergrid.be/fr/au-sujet-des-reseaux/le-grd-dans-votre-commune'
            : 'https://www.synergrid.be/nl/over-de-netten/dnb-in-uw-gemeente'
      }
    }
  },
  [DynamicPriceQuizResults.UNAVAILABLE_GENERAL]: {
    title: 'Je komt helaas niet in aanmerking voor dynamische prijzen',
    description:
      'Opgelet, sinds 2020 bouwt energieregulator VREG het financiële voordeel van het exclusief nachttarief geleidelijk af over een periode van acht jaar. Vanaf 2028 zal het exclusief nachttarief in België verdwijnen.',
    actions: {
      primary: {
        text: 'Bekijk onze andere tarieven',
        internalRoute: 'pricing'
      }
    }
  },
  [DynamicPriceQuizResults.INTERESTING]: {
    title: 'Dynamische prijzen zijn voor jou',
    rating: {
      text: 'interessant',
      state: 'positive'
    },
    description:
      'Jij kan een groot deel van jouw energieverbruik manueel, via een slimme laadpaal of via een thuisbatterij plannen of sturen op de meest voordelige momenten.',
    actions: {
      primary: {
        text: 'Stap over naar Bolt',
        action: () => startSimulationOrRegistration()
      },
      secondary: {
        text: 'Activeer in My Bolt',
        externalUrl: () => ACTIVATE_IN_PORTAL_URL
      }
    }
  },
  [DynamicPriceQuizResults.CONSIDERABLE]: {
    title: 'Dynamische prijzen zijn voor jou',
    rating: {
      text: 'het overwegen waard',
      state: 'informative'
    },
    description:
      'Dynamische prijzen zijn interessant als je grote verbruiken kan plannen. Maar opgelet, als je jouw energieverbruik niet kan plannen of verschuiven, dan kan je met dynamische prijzen duurder uitkomen. Neem contact op via 02 899 33 00 voor persoonlijk advies.',
    actions: {
      primary: {
        text: 'Stap over naar Bolt',
        action: () => startSimulationOrRegistration()
      },
      secondary: {
        text: 'Activeer in My Bolt',
        externalUrl: () => ACTIVATE_IN_PORTAL_URL
      }
    }
  },
  [DynamicPriceQuizResults.NOT_INTERESTING]: {
    title: 'Dynamische prijzen zijn voor jou',
    rating: {
      text: 'niet interessant',
      state: 'negative'
    },
    description:
      'Voor jou is een traditioneel energietarief (vast of variabel) nog steeds de beste keuze. Als je niet in staat bent om flexibel te zijn in het plannen of verschuiven van je energieverbruik, dan kan je zelfs duurder uitkomen met dynamische prijzen.',
    actions: {
      primary: {
        text: 'Stap over naar Bolt',
        action: () => startSimulationOrRegistration()
      },
      secondary: {
        text: 'Activeer in My Bolt',
        externalUrl: () => ACTIVATE_IN_PORTAL_URL
      }
    }
  }
}

export const QUIZ_FLOW: StackingCardsFlow<DynamicPriceQuizQuestions, DynamicPriceQuizResults> = [
  {
    question: DynamicPriceQuizQuestions.LOCATION,
    actions: {
      yes: {
        next: () => DynamicPriceQuizQuestions.METER,
        text: 'Ja',
        icon: 'check',
        value: true
      },
      no: {
        next: () => DynamicPriceQuizResults.UNAVAILABLE_FLANDERS_ONLY,
        text: 'Nee',
        icon: 'cross',
        value: false
      }
    }
  },
  {
    question: DynamicPriceQuizQuestions.METER,
    actions: {
      yes: {
        next: () => DynamicPriceQuizQuestions.SOLAR_PANELS_OR_HOME_BATTERY,
        text: 'Ja',
        icon: 'check',
        value: true
      },
      no: {
        next: () => DynamicPriceQuizResults.UNAVAILABLE_DIGITAL_METER_REQUIRED,
        text: 'Nee',
        icon: 'cross',
        value: false
      }
    }
  },
  {
    question: DynamicPriceQuizQuestions.SOLAR_PANELS_OR_HOME_BATTERY,
    actions: {
      yes: {
        next: () => DynamicPriceQuizQuestions.ELECTRIC_VEHICLE,
        text: 'Ja',
        icon: 'check',
        value: true
      },
      no: {
        next: () => DynamicPriceQuizQuestions.ELECTRIC_VEHICLE,
        text: 'Nee',
        icon: 'cross',
        value: false
      }
    }
  },
  {
    question: DynamicPriceQuizQuestions.ELECTRIC_VEHICLE,
    actions: {
      yes: {
        next: () => DynamicPriceQuizQuestions.SMART_HOME,
        text: 'Ja',
        icon: 'check',
        value: true // Higher weight due to score result logic
      },
      no: {
        next: () => DynamicPriceQuizQuestions.SMART_HOME,
        text: 'Nee',
        icon: 'cross',
        value: false
      }
    }
  },
  {
    question: DynamicPriceQuizQuestions.SMART_HOME,
    actions: {
      yes: {
        next: calculateScoreForResult,
        text: 'Ja',
        icon: 'check',
        value: true
      },
      no: {
        next: calculateScoreForResult,
        text: 'Nee',
        icon: 'cross',
        value: false
      }
    }
  }
]
